/** The order header's restech chip (plan §1.1, §2.4): which reservation system created
 *  the order, through which account, and when it last fed it — "fed 2 hours ago", from
 *  the order's own updated time in that system (`remote_updated_ts`; an order has no feed
 *  fingerprint). The tooltip gives that time, labelled, how many of the order's fields the
 *  system's order webhook rewrites on every update (`restech.rewritten_fields` — the
 *  ledger banner's count too) and what booking imports do besides. Without a remote
 *  updated time the chip names the rewrite count instead. The chip opens the Integration
 *  tab. Absent for an order no integration created. */
import SyncIcon from '@mui/icons-material/Sync';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import type { OrderRecordRestech } from '@/api/orderRecord';
import { formatRelative, formatZoned } from '@/utils/zonedTime';

import { ORDER_TIMEZONE } from './views/spineModel';

export function OrderRestechChip({
  restech,
  remoteUpdatedTs,
  integrationHref,
}: {
  restech: OrderRecordRestech;
  /** When the reservation system last updated the order (the order detail's). */
  remoteUpdatedTs: string | null | undefined;
  /** The Integration tab of this order. */
  integrationHref: string;
}) {
  if (!restech.managed_by) return null;
  const name = restech.managed_by_display ?? restech.managed_by;
  const n = restech.rewritten_fields.length;
  const rewrites = n > 0 ? `rewrites ${n} ${n === 1 ? 'field' : 'fields'}` : 'keeps manual edits';
  const status = remoteUpdatedTs ? `fed ${formatRelative(remoteUpdatedTs)}` : rewrites;
  const lines = [
    remoteUpdatedTs ? `Last updated in ${name}: ${formatZoned(remoteUpdatedTs, ORDER_TIMEZONE)}` : null,
    n > 0
      ? `${name}’s order webhook rewrites ${n} order ${n === 1 ? 'field' : 'fields'} on every update of the order`
      : `${name} does not rewrite this order’s fields`,
    restech.imported
      ? 'Booking imports also write it: they fill a field only while it is empty and reconcile order tags only in the categories they send'
      : null,
    'Open the Integration tab',
  ].filter(Boolean) as string[];

  return (
    <Tooltip
      title={
        <Box sx={{ display: 'grid', gap: 0.25 }}>
          {lines.map((l) => (
            <Typography key={l} variant="caption" component="div">
              {l}
            </Typography>
          ))}
        </Box>
      }
    >
      <Chip
        size="small"
        variant="outlined"
        clickable
        component={RouterLink}
        to={integrationHref}
        icon={<SyncIcon />}
        data-restech-chip
        aria-label={`${name}${restech.account ? `, account ${restech.account}` : ''}, ${status}. ${lines.join('. ')}`}
        label={
          <span>
            <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {name}
            </Box>
            {restech.account ? ` · ${restech.account}` : ''} · {status}
          </span>
        }
        sx={{ color: 'text.secondary', fontWeight: 500 }}
      />
    </Tooltip>
  );
}
